import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { FormField } from "../shared/FormField";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { postWithVersion, isVersionConflict, conflictMessage } from "../../lib/conflict";

export function ChangeDriverDialog({ open, onOpenChange, routeId, currentDriverId, currentVersion, onDone, onConflict }) {
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDriverId("");
    setConflict(null);
    apiClient.get("/drivers", { params: { limit: 200 } })
      .then(({ data }) => setDrivers(data.items.filter((d) => d.id !== currentDriverId)))
      .catch(() => setDrivers([]));
  }, [open, currentDriverId]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await postWithVersion(`/routes/${routeId}/change-driver`, { driver_id: Number(driverId) }, currentVersion);
      toast.success("Driver changed for this route");
      onDone();
    } catch (error) {
      if (isVersionConflict(error)) {
        setConflict(conflictMessage(error));
        toast.warning(conflictMessage(error));
        onConflict && onConflict();  // parent reloads so currentVersion refreshes for a retry
      } else {
        toast.error(getApiErrorMessage(error, "Could not change the driver."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-driver-dialog">
        <DialogHeader>
          <DialogTitle>Change Driver</DialogTitle>
          <DialogDescription>Reassigns this route and every one of its stops to the new driver.</DialogDescription>
        </DialogHeader>
        {conflict && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="change-driver-conflict-banner">
            <p className="font-medium text-amber-900">{conflict}</p>
            <p className="mt-1 text-xs text-amber-800">
              We've reloaded the latest route — your driver choice is kept. Press Change Driver again to apply it over the latest.
            </p>
          </div>
        )}
        <FormField label="New Driver" testId="change-driver-field">
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger data-testid="change-driver-select"><SelectValue placeholder="Select a driver..." /></SelectTrigger>
            <SelectContent>
              {drivers.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name || `Driver #${d.id}`}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="change-driver-cancel-button">Cancel</Button>
          <Button variant="accent" disabled={saving || !driverId} onClick={handleConfirm} data-testid="change-driver-confirm-button">
            {saving ? "Saving..." : "Change Driver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
